/**
 * @fileoverview Zod schemas for the saved-events API response.
 *
 * Mirrors the canonical TypeScript interfaces in
 * `@loci/types/saved-events` (`SavedEventsApiResponse` and friends) and
 * validates the response body of
 * `GET /api/v1/analytics/user/:userId/saved` and
 * `GET /api/v1/users/me/following/saved`.
 */

import { z } from 'zod';

export const savedEventPayloadSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  date: z.string(),
  imageUrl: z.string().optional(),
  ticketUrl: z.string().optional(),
  price: z.string().nullish(),
  location: z
    .object({
      name: z.string(),
      city: z.string().nullish(),
      state: z.string().nullish(),
    })
    .nullish(),
  categories: z.array(z.string()),
  artists: z.array(z.string()),
});

export const savedEventApiItemSchema = z.object({
  savedAt: z.string(),
  event: savedEventPayloadSchema.nullable(),
});

export const savedEventsPaginationSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  hasMore: z.boolean(),
});

export const savedEventsResponseSchema = z.object({
  savedEvents: z.array(savedEventApiItemSchema),
  pagination: savedEventsPaginationSchema,
});

/**
 * Saved event payload embedded in each saved-events item.
 */
export type SavedEventPayload = z.infer<typeof savedEventPayloadSchema>;

/**
 * Single saved-events entry with timestamp and nullable event payload.
 */
export type SavedEventApiItem = z.infer<typeof savedEventApiItemSchema>;

/**
 * Pagination metadata returned alongside saved events.
 */
export type SavedEventsPagination = z.infer<typeof savedEventsPaginationSchema>;

/**
 * Full response body for GET /api/v1/analytics/user/:userId/saved.
 */
export type SavedEventsApiResponse = z.infer<typeof savedEventsResponseSchema>;
